import React from 'react';
import { Skeleton } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { daysUntil, formatBRL, formatDate } from '../wallet-section/walletFormat';
import { isOverdue } from '../payments/paymentState';
import styles from './FinancialOverview.module.css';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/**
 * Resumo da aba: a receber, recebido, sessões disponíveis e, quando existem,
 * próximo vencimento e saldo em dinheiro. Responde à primeira pergunta de quem
 * abre a aba ("deve alguma coisa? ainda tem sessão?") antes dos detalhes.
 */
function FinancialOverview({ open, paid, stats, wallet }) {
  const overdue = open.filter(isOverdue).length;
  const toReceive = Number(stats?.torecive) || 0;
  const received = Number(stats?.paid) || 0;
  const { summary, monetaryBalance, loading } = wallet;
  const days = daysUntil(summary.nextExpiration);
  const expirationSoon = days !== null && days <= 30;

  const tiles = [
    {
      key: 'open',
      Icon: ReceiptLongOutlinedIcon,
      label: 'A receber',
      value: formatBRL(toReceive),
      detail: overdue > 0
        ? plural(overdue, 'atendimento sem pagamento', 'atendimentos sem pagamento')
        : (open.length > 0 ? plural(open.length, 'agendado', 'agendados') : 'Nada em aberto'),
      tone: overdue > 0 ? 'warning' : 'neutral',
    },
    {
      key: 'paid',
      Icon: CheckCircleOutlineRoundedIcon,
      label: 'Recebido',
      value: formatBRL(received),
      detail: paid.length > 0 ? plural(paid.length, 'pagamento', 'pagamentos') : 'Nenhum pagamento',
      tone: 'success',
    },
    {
      key: 'sessions',
      Icon: ConfirmationNumberOutlinedIcon,
      label: 'Sessões disponíveis',
      value: loading ? null : String(summary.sessions),
      detail: loading
        ? null
        : (summary.activeCount > 0 ? plural(summary.activeCount, 'pacote em uso', 'pacotes em uso') : 'Nenhum pacote em uso'),
      tone: 'blue',
    },
  ];

  if (!loading && summary.nextExpiration) {
    tiles.push({
      key: 'expiration',
      Icon: EventAvailableOutlinedIcon,
      label: 'Próximo vencimento',
      value: formatDate(summary.nextExpiration),
      detail: days === 0 ? 'vence hoje' : (days === 1 ? 'em 1 dia' : `em ${days} dias`),
      tone: expirationSoon ? 'warning' : 'blue',
    });
  }

  if (!loading && monetaryBalance > 0) {
    tiles.push({
      key: 'balance',
      Icon: AccountBalanceWalletOutlinedIcon,
      label: 'Saldo em dinheiro',
      value: formatBRL(monetaryBalance),
      detail: 'crédito para abater em faturas',
      tone: 'blue',
    });
  }

  return (
    <div className={styles.grid} aria-label="Resumo financeiro">
      {tiles.map(({ key, Icon, label, value, detail, tone }) => (
        <div key={key} className={`${styles.tile} ${styles[tone]}`}>
          <span className={styles.icon}><Icon fontSize="small" /></span>
          <div className={styles.text}>
            <span className={styles.label}>{label}</span>
            {value === null
              ? <Skeleton variant="text" width={56} height={30} />
              : <span className={styles.value}>{value}</span>}
            {detail === null
              ? <Skeleton variant="text" width={110} height={16} />
              : <span className={styles.detail}>{detail}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default FinancialOverview;
